import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import { BrandMark } from '@/components/generals/brand-mark'
import { mainNav, siteConfig } from '@/constants/site'

export const SiteHeader: FC = (): ReactNode => {
    const router = useRouter()
    const [open, setOpen] = useState(false)

    const isActive = (href: string): boolean => {
        return router.pathname === href || router.pathname.startsWith(`${href}/`)
    }

    useEffect(() => {
        const close = () => setOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        document.addEventListener('keydown', onKeyDown)
        document.body.style.overflow = 'hidden'

        return () => {
            document.removeEventListener('keydown', onKeyDown)
            document.body.style.overflow = ''
        }
    }, [open])

    return (
        <header className='sticky top-0 z-40 border-b border-border/70 bg-background/90 backdrop-blur-md'>
            <div className='mx-auto flex h-18 max-w-7xl items-center justify-between gap-6 px-5 sm:px-8'>
                <Link
                    href='/'
                    aria-label={`${siteConfig.name}, home`}
                    className='group flex items-center gap-3 rounded-md outline-none focus-visible:ring-3 focus-visible:ring-ring/40'
                >
                    <BrandMark className='size-9 shrink-0 text-primary transition-transform duration-500 ease-out group-hover:-translate-y-0.5' />

                    <span className='flex flex-col leading-none'>
                        <span className='font-heading text-xl tracking-tight'>
                            {siteConfig.name}
                        </span>

                        <span className='mt-1.5 hidden text-[0.68rem] uppercase tracking-[0.24em] text-muted-foreground sm:block'>
                            {siteConfig.tagline}
                        </span>
                    </span>
                </Link>

                <nav aria-label="Main" className='hidden items-center gap-9 md:flex'>
                    {mainNav.map(item => (
                        <Link
                            key={item.href}
                            href={item.href}
                            aria-current={isActive(item.href) ? 'page' : undefined}
                            className={cn(
                                'relative py-1 text-[0.95rem] text-foreground/70 transition-colors hover:text-foreground',
                                'after:absolute after:inset-x-0 after:-bottom-0.5 after:h-px after:origin-left after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:scale-x-100',
                                isActive(item.href) && 'text-foreground after:scale-x-100'
                            )}
                        >
                            {item.label}
                        </Link>
                    ))}

                    <Link href='/contact' className={buttonVariants({ size: 'sm' })}>
                        Work with me
                    </Link>
                </nav>

                <button
                    type='button'
                    aria-expanded={open}
                    aria-controls='mobile-menu'
                    aria-label={open ? "Close menu" : "Open menu"}
                    onClick={() => setOpen(value => !value)}
                    className='-mr-2 inline-flex size-11 items-center justify-center rounded-full text-foreground transition-colors hover:bg-foreground/5 md:hidden'
                >
                    {open ? <X className='size-6' /> : <Menu className='size-6' />}
                </button>
            </div>

            {open && (
                <div
                    id='mobile-menu'
                    className='absolute inset-x-0 top-full h-[calc(100dvh-4.5rem)] overflow-y-auto border-t border-border/70 bg-background animate-in fade-in slide-in-from-top-2 duration-200 md:hidden'
                >
                    <nav aria-label="Mobile" className='mx-auto flex max-w-7xl flex-col px-5 pt-4 pb-10 sm:px-8'>
                        <Link
                            href='/'
                            aria-current={router.pathname === '/' ? 'page' : undefined}
                            className='border-b border-border/70 py-4 font-heading text-3xl'
                        >
                            Home
                        </Link>

                        {mainNav.map(item => (
                            <Link
                                key={item.href}
                                href={item.href}
                                aria-current={isActive(item.href) ? 'page' : undefined}
                                className={cn(
                                    'border-b border-border/70 py-4 font-heading text-3xl',
                                    isActive(item.href) && 'text-primary'
                                )}
                            >
                                {item.label}
                            </Link>
                        ))}

                        <Link href='/contact' className={buttonVariants({ size: 'lg', className: 'mt-8 w-full' })}>
                            Work with me
                        </Link>
                    </nav>
                </div>
            )}
        </header>
    )
}
